const normalizeText = (value) =>
  typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';

const extractContentText = (content) => {
  if (!content) return '';
  if (typeof content === 'string') return normalizeText(content);
  if (Array.isArray(content)) {
    return normalizeText(content.map(extractContentText).filter(Boolean).join('\n'));
  }
  if (typeof content !== 'object') return '';

  if (Array.isArray(content.parts)) {
    return extractContentText(content.parts);
  }
  if (typeof content.text === 'string') {
    return normalizeText(content.text);
  }
  if (typeof content.value === 'string') {
    return normalizeText(content.value);
  }
  if (typeof content.content === 'string' || Array.isArray(content.content)) {
    return extractContentText(content.content);
  }

  return '';
};

const normalizeRole = (role) => {
  const value = normalizeText(role).toLowerCase();
  if (['user', 'human'].includes(value)) return 'user';
  if (['assistant', 'ai'].includes(value)) return 'assistant';
  return null;
};

const parseChatGptConversation = (conversation, index) => {
  const messages = [];
  const mapping = conversation?.mapping && typeof conversation.mapping === 'object'
    ? Object.values(conversation.mapping)
    : [];

  mapping
    .map((node) => node?.message)
    .filter(Boolean)
    .sort((a, b) => (a.create_time || 0) - (b.create_time || 0))
    .forEach((message, messageIndex) => {
      const role = normalizeRole(message?.author?.role);
      const text = extractContentText(message?.content);
      if (!role || !text) return;

      messages.push({
        id: normalizeText(message.id) || `chatgpt_${index}_${messageIndex}`,
        role,
        text,
      });
    });

  return {
    id: normalizeText(conversation?.id) || normalizeText(conversation?.conversation_id) || `chatgpt_${index}`,
    title: normalizeText(conversation?.title),
    messages,
  };
};

const parseClaudeConversation = (conversation, index) => {
  const rawMessages = Array.isArray(conversation?.chat_messages)
    ? conversation.chat_messages
    : Array.isArray(conversation?.messages)
      ? conversation.messages
      : [];

  const messages = rawMessages.map((message, messageIndex) => {
    const role = normalizeRole(message?.sender || message?.role || message?.author);
    const text = extractContentText(message?.text || message?.content);
    if (!role || !text) return null;
    return {
      id: normalizeText(message.uuid) || normalizeText(message.id) || `claude_${index}_${messageIndex}`,
      role,
      text,
    };
  }).filter(Boolean);

  return {
    id: normalizeText(conversation?.uuid) || normalizeText(conversation?.id) || `claude_${index}`,
    title: normalizeText(conversation?.name) || normalizeText(conversation?.title),
    messages,
  };
};

const getConversationArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.conversations)) return data.conversations;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const parseExternalMemoryExport = (data, preferredSource = 'auto') => {
  const conversations = getConversationArray(data);
  if (conversations.length === 0) {
    throw new Error('No conversations found in this export file');
  }

  const first = conversations[0] || {};
  const detectedSource = first.mapping
    ? 'chatgpt'
    : first.chat_messages || first.uuid
      ? 'claude'
      : preferredSource === 'claude'
        ? 'claude'
        : 'chatgpt';
  const source = preferredSource === 'auto' ? detectedSource : preferredSource;
  const parser = source === 'claude' ? parseClaudeConversation : parseChatGptConversation;
  const normalized = conversations
    .map(parser)
    .filter((conversation) => conversation.messages.length > 0);

  if (normalized.length === 0) {
    throw new Error('No importable user or assistant messages were found');
  }

  return { source, conversations: normalized };
};

export const chunkConversationsForImport = (conversations, maxMessagesPerChunk = 250) => {
  const chunks = [];
  let current = [];
  let currentMessageCount = 0;

  conversations.forEach((conversation) => {
    if (conversation.messages.length > maxMessagesPerChunk) {
      if (current.length > 0) {
        chunks.push(current);
        current = [];
        currentMessageCount = 0;
      }

      for (let index = 0; index < conversation.messages.length; index += maxMessagesPerChunk) {
        chunks.push([
          {
            ...conversation,
            id: `${conversation.id}_${index}`,
            messages: conversation.messages.slice(index, index + maxMessagesPerChunk),
          },
        ]);
      }
      return;
    }

    if (currentMessageCount + conversation.messages.length > maxMessagesPerChunk && current.length > 0) {
      chunks.push(current);
      current = [];
      currentMessageCount = 0;
    }

    current.push(conversation);
    currentMessageCount += conversation.messages.length;
  });

  if (current.length > 0) chunks.push(current);
  return chunks;
};
